const STORAGE_KEY = 'texas42:error-log';
const MAX_ENTRIES = 100;

export interface ErrorLogEntry {
  id: string;
  timestamp: string;
  kind: 'error' | 'rejection';
  message: string;
  stack?: string;
  source?: string;
}

function readStoredEntries(): ErrorLogEntry[] {
  if (typeof localStorage === 'undefined') return [];

  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as ErrorLogEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeStoredEntries(entries: ErrorLogEntry[]): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(entries.slice(-MAX_ENTRIES)));
}

export function listErrorLogEntries(): ErrorLogEntry[] {
  return readStoredEntries();
}

export function clearErrorLog(): void {
  if (typeof localStorage === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

export function appendErrorLogEntry(
  entry: Omit<ErrorLogEntry, 'id' | 'timestamp'> & Partial<Pick<ErrorLogEntry, 'id' | 'timestamp'>>,
): void {
  const next: ErrorLogEntry = {
    id: entry.id ?? crypto.randomUUID(),
    timestamp: entry.timestamp ?? new Date().toISOString(),
    kind: entry.kind,
    message: entry.message,
    ...(entry.stack ? { stack: entry.stack } : {}),
    ...(entry.source ? { source: entry.source } : {}),
  };

  writeStoredEntries([...readStoredEntries(), next]);
}

export function installGlobalErrorLogging(): () => void {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  const onError = (event: ErrorEvent) => {
    appendErrorLogEntry({
      kind: 'error',
      message: event.message || 'Unknown error',
      ...(event.error instanceof Error && event.error.stack ? { stack: event.error.stack } : {}),
      ...(event.filename ? { source: `${event.filename}:${event.lineno}` } : {}),
    });
  };

  const onRejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason;
    appendErrorLogEntry({
      kind: 'rejection',
      message: reason instanceof Error ? reason.message : String(reason),
      ...(reason instanceof Error && reason.stack ? { stack: reason.stack } : {}),
    });
  };

  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);

  return () => {
    window.removeEventListener('error', onError);
    window.removeEventListener('unhandledrejection', onRejection);
  };
}
